import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  Quiz as QuizIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { getQuizAnalytics } from '../../services/teacherService';
import { QuizAnalytics } from '../../types/teacher';

interface QuizPerformanceCardProps {
  refreshTrigger?: number;
}

const QuizPerformanceCard: React.FC<QuizPerformanceCardProps> = ({ refreshTrigger }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<QuizAnalytics | null>(null);

  useEffect(() => {
    fetchQuizAnalytics();
  }, [refreshTrigger]);

  const fetchQuizAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getQuizAnalytics();
      setAnalytics(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load quiz analytics';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getCompletionColor = (rate: number): 'success' | 'warning' | 'error' => {
    if (rate >= 70) return 'success';
    if (rate >= 50) return 'warning';
    return 'error';
  };

  const getScoreColor = (score: number): 'success' | 'warning' | 'error' | 'default' => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    if (score > 0) return 'error';
    return 'default';
  };

  return (
    <Card elevation={2}>
      <CardHeader
        avatar={<QuizIcon color="primary" />}
        title="Quiz Performance Analytics"
        subheader="Class-wide quiz completion and performance metrics"
      />
      <CardContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !analytics || analytics.totalQuizzes === 0 ? (
          <Alert severity="info">
            No quiz data available yet. Quiz analytics will appear here once students start taking quizzes.
          </Alert>
        ) : (
          <>
            {/* Overall Summary Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center" p={2} bgcolor="background.default" borderRadius={2}>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {analytics.totalQuizzes}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Quizzes
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center" p={2} bgcolor="background.default" borderRadius={2}>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {analytics.averageScore}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Average Score
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center" p={2} bgcolor="background.default" borderRadius={2}>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {analytics.averageCompletionRate}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Completion Rate
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center" p={2} bgcolor="background.default" borderRadius={2}>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {analytics.studentsWithAttempts}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Students Participating
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Activity Summary */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Activity Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <TrendingUpIcon color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Total Attempts: <strong>{analytics.totalAttempts}</strong>
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CheckCircleIcon color="success" />
                    <Typography variant="body2" color="text.secondary">
                      Completed Attempts: <strong>{analytics.completedAttempts}</strong>
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Per-Quiz Breakdown */}
            <Typography variant="h6" gutterBottom>
              Individual Quiz Performance
            </Typography>
            {analytics.quizzes.length === 0 ? (
              <Alert severity="info">No individual quiz data available</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Quiz Title</strong></TableCell>
                      <TableCell align="center"><strong>Attempts</strong></TableCell>
                      <TableCell align="center"><strong>Completed</strong></TableCell>
                      <TableCell align="center"><strong>Avg Score</strong></TableCell>
                      <TableCell align="center"><strong>Completion Rate</strong></TableCell>
                      <TableCell align="center"><strong>Avg Time</strong></TableCell>
                      <TableCell align="center"><strong>High/Low</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.quizzes.map((quiz) => (
                      <TableRow key={quiz.quizId} hover>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {quiz.quizTitle}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">{quiz.totalAttempts}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                            <CheckCircleIcon fontSize="small" color="action" />
                            <Typography variant="body2">{quiz.completedAttempts}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${quiz.averageScore}%`}
                            size="small"
                            color={getScoreColor(quiz.averageScore)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${quiz.completionRate}%`}
                            size="small"
                            color={getCompletionColor(quiz.completionRate)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                            <AccessTimeIcon fontSize="small" color="action" />
                            <Typography variant="body2">{quiz.averageTimeMinutes}m</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="caption" color="text.secondary">
                            {quiz.highestScore > 0 ? `${quiz.highestScore}` : '-'} /
                            {quiz.lowestScore > 0 ? ` ${quiz.lowestScore}` : ' -'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default QuizPerformanceCard;
